import React, { useState } from 'react';
import { Lock, Calendar, Image, FileText } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

const ParentArea = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [credentials, setCredentials] = useState({ email: '', password: '' });

  const handleLogin = (e) => {
    e.preventDefault();
    // Mock login
    if (credentials.email && credentials.password) {
      toast.success('Accesso effettuato con successo!');
      setIsLoggedIn(true);
    } else {
      toast.error('Inserisci email e password');
    }
  };

  if (!isLoggedIn) {
    return (
      <section id="parent-area" className="py-20 bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="container mx-auto px-4">
          <div className="max-w-md mx-auto">
            <Card className="border-0 shadow-2xl">
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 mx-auto bg-gradient-to-br from-pink-100 to-purple-100 rounded-full flex items-center justify-center mb-4">
                    <Lock className="w-8 h-8 text-pink-600" />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">
                    Area Riservata Genitori
                  </h2>
                  <p className="text-gray-600">
                    Accedi per vedere foto, aggiornamenti e comunicazioni
                  </p>
                </div>

                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <Input
                      type="email"
                      required
                      value={credentials.email}
                      onChange={(e) => setCredentials({...credentials, email: e.target.value})}
                      placeholder="vostra.email@esempio.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Password
                    </label>
                    <Input
                      type="password"
                      required
                      value={credentials.password}
                      onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                      placeholder="••••••••"
                    />
                  </div>

                  <Button 
                    type="submit"
                    className="w-full bg-[#E91E8C] hover:bg-[#d01a7a] text-white py-6 rounded-full"
                  >
                    Accedi
                  </Button>

                  <p className="text-center text-sm text-gray-600">
                    Non hai le credenziali? Contatta la segreteria
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="parent-area" className="py-20 bg-gradient-to-br from-purple-50 to-pink-50">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-4xl font-bold text-gray-900">Area Riservata Genitori</h2>
            <Button 
              variant="outline"
              onClick={() => setIsLoggedIn(false)}
              className="border-pink-600 text-pink-600 hover:bg-pink-50"
            >
              Esci
            </Button>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Calendars */}
            <Card className="border-0 shadow-xl">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-pink-100 to-purple-100 rounded-full flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-pink-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Calendario Scolastico</h3>
                </div>
                <p className="text-gray-600 mb-4">Visualizza aperture e chiusure annuali</p>
                <div className="bg-gray-100 rounded-lg p-8 text-center">
                  <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Calendario disponibile a breve</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-xl">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-blue-100 rounded-full flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Prossimi Eventi</h3>
                </div>
                <p className="text-gray-600 mb-4">Attività speciali e open day</p>
                <div className="bg-gray-100 rounded-lg p-8 text-center">
                  <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Eventi disponibili a breve</p>
                </div>
              </CardContent>
            </Card>

            {/* Photo Gallery */}
            <Card className="border-0 shadow-xl">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center">
                    <Image className="w-6 h-6 text-purple-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Galleria Foto</h3>
                </div>
                <p className="text-gray-600 mb-4">Le foto delle attività del vostro bambino</p>
                <div className="bg-gray-100 rounded-lg p-8 text-center">
                  <Image className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Foto disponibili a breve</p>
                </div>
              </CardContent>
            </Card>

            {/* Communications */}
            <Card className="border-0 shadow-xl">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-pink-100 to-purple-100 rounded-full flex items-center justify-center">
                    <FileText className="w-6 h-6 text-pink-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Comunicazioni</h3>
                </div>
                <p className="text-gray-600 mb-4">Avvisi e novità dal nido</p>
                <div className="bg-gray-100 rounded-lg p-8 text-center">
                  <FileText className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Nessuna nuova comunicazione</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ParentArea;
